import { orderBy } from "lodash";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import * as FlatStates from "../../internal";
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const VOCABULARY_WORD_ENTITY_TOKEN = new StateToken<VocabularyWordEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.VOCABULARY_WORD_ENTITY);

export type VocabularyWordEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.VocabularyWordEntityModels.VocabularyWord>;

@State<VocabularyWordEntityStateModel>({
  name: VOCABULARY_WORD_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class VocabularyWordEntityState extends HistoryEntityBaseState<FlatModels.VocabularyWordEntityModels.VocabularyWord, VocabularyWordEntityStateModel> {
  //<editor-fold desc="Selectors">
  
  @Selector([VocabularyWordEntityState])
  static override typedEntities$(state: VocabularyWordEntityStateModel): { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
  
  @Selector([VocabularyWordEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord }): FlatModels.VocabularyWordEntityModels.VocabularyWord[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
  
  @Selector([VocabularyWordEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
  
  @Selector([VocabularyWordEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
  
  @Selector([
    FlatStates.VocabularyGroupEntityState.currentSelectedVocabularyGroups$,
    VocabularyWordEntityState.typedEntities$
  ])
  static currentSelectedVocabularyWord$(currentSelectedVocabularyGroups: FlatModels.VocabularyGroupEntityModels.VocabularyGroup[],
                                        typedEntities: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord }) {
    return currentSelectedVocabularyGroups.reduce((garr: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord[]}, group) => {
      garr[group.id] = orderBy((group.vocabularyWords || []).reduce((warr: FlatModels.VocabularyWordEntityModels.VocabularyWord[], word) => {
        const entity = typedEntities[word];
        if (entity) {
          warr.push(entity);
        }
        return warr;
      }, []), (a) => a.value || 0, 'asc');
      return garr;
    }, {})
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
  
  @Action(FlatActions.VocabularyWordEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<VocabularyWordEntityStateModel>,
                            action: FlatActions.VocabularyWordEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<VocabularyWordEntityStateModel>,
                              action: FlatActions.VocabularyWordEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<VocabularyWordEntityStateModel>,
                               action: FlatActions.VocabularyWordEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<VocabularyWordEntityStateModel>,
                                 action: FlatActions.VocabularyWordEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
  
  @Action(FlatActions.VocabularyWordEntityActions.AddEntity)
  @Undoable(
    FlatActions.VocabularyWordEntityActions.AddEntity,
    VOCABULARY_WORD_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<VocabularyWordEntityStateModel>,
                     action: FlatActions.VocabularyWordEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.VocabularyWordEntityActions.UpdateEntity,
    VOCABULARY_WORD_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<VocabularyWordEntityStateModel>,
                        action: FlatActions.VocabularyWordEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.VocabularyWordEntityActions.RemoveEntity,
    VOCABULARY_WORD_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<VocabularyWordEntityStateModel>,
                        action: FlatActions.VocabularyWordEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">
  
  @Action(FlatActions.VocabularyWordEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<VocabularyWordEntityStateModel>,
                        action: FlatActions.VocabularyWordEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<VocabularyWordEntityStateModel>,
                        action: FlatActions.VocabularyWordEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
  
  @Action(FlatActions.VocabularyWordEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<VocabularyWordEntityStateModel>,
                            action: FlatActions.VocabularyWordEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
